package co.maytes.standupbot.dashboard.components.widgets

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Typography
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.CSSColorValue
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Img

enum class AvatarTone { Neutral, Accent, Muted }

@Composable
fun Avatar(
    name: String,
    src: String? = null,
    sizePx: Int = 32,
    tone: AvatarTone = AvatarTone.Neutral,
    modifier: Modifier = Modifier,
) {
    var loadError by remember(src) { mutableStateOf(false) }
    val showImage = !src.isNullOrBlank() && !loadError
    val bg: CSSColorValue = when (tone) {
        AvatarTone.Neutral -> Palette.primary
        AvatarTone.Accent -> Palette.accentSubtle
        AvatarTone.Muted -> Palette.surfaceSubtle
    }
    val fg: CSSColorValue = when (tone) {
        AvatarTone.Neutral -> Palette.onPrimary
        AvatarTone.Accent -> Palette.accent
        AvatarTone.Muted -> Palette.textTertiary
    }

    Box(
        modifier = Modifier
            .width(sizePx.px)
            .height(sizePx.px)
            .backgroundColor(bg)
            .borderRadius(Radius.full)
            .styleModifier {
                property("flex-shrink", "0")
                property("overflow", "hidden")
                property("position", "relative")
            }
            .then(modifier),
        contentAlignment = Alignment.Center,
    ) {
        SpanText(
            text = computeInitials(name),
            modifier = Modifier
                .color(fg)
                .fontSize((sizePx * 0.38).px)
                .fontWeight(600)
                .styleModifier { property("font-family", Typography.FONT_MONO) },
        )
        if (showImage) {
            Img(
                src = src!!,
                attrs = Modifier
                    .width(sizePx.px)
                    .height(sizePx.px)
                    .styleModifier {
                        property("object-fit", "cover")
                        property("display", "block")
                        property("position", "absolute")
                        property("inset", "0")
                    }
                    .toAttrs {
                        attr("alt", name)
                        attr("loading", "lazy")
                        attr("referrerpolicy", "no-referrer")
                        ref { img ->
                            img.addEventListener("error", { loadError = true })
                            onDispose { }
                        }
                    },
            )
        }
    }
}

private fun computeInitials(name: String): String {
    val parts = name.trim()
        .removePrefix("@")
        .split(" ", "-", "_", ".")
        .filter { it.isNotBlank() }
    return when {
        parts.isEmpty() -> "?"
        parts.size == 1 -> parts[0].take(2).uppercase()
        else -> (parts[0].take(1) + parts.last().take(1)).uppercase()
    }
}
